/**
 * UndoSnackbar — Phase 3.86+ (Feb 2026)
 *
 * Notion/Linear-Style „Letzte Aktion rückgängig"-Snackbar.
 * Wird unten links eingeblendet, läuft 10 Sekunden runter (visuelle Bar),
 * verschwindet dann automatisch. Klick auf „Rückgängig" ruft `onUndo` auf.
 *
 * Bedienung:
 *   <UndoSnackbar open={undoVisible} label="Marktplatz gespeichert" onUndo={revert} onTimeout={() => setUndoVisible(false)} />
 */
import { useEffect, useState } from "react";
import { Undo2, X } from "lucide-react";

const COUNTDOWN_MS = 10000;
const TICK_MS = 100;

export default function UndoSnackbar({ open, label, onUndo, onTimeout, secondsTotal = 10 }) {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (!open) return undefined;
        setProgress(0);
        const start = Date.now();
        const total = secondsTotal * 1000;
        const interval = setInterval(() => {
            const elapsed = Date.now() - start;
            const pct = Math.min(100, (elapsed / total) * 100);
            setProgress(pct);
            if (elapsed >= total) {
                clearInterval(interval);
                onTimeout?.();
            }
        }, TICK_MS);
        return () => clearInterval(interval);
    }, [open, secondsTotal, onTimeout]);

    if (!open) return null;
    const secondsLeft = Math.max(0, Math.ceil(secondsTotal - (progress * secondsTotal / 100)));

    return (
        <div
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]"
            data-testid="undo-snackbar"
        >
            <div className="bg-[#111827] text-white rounded-sm shadow-2xl border border-gray-700 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00]">✓</div>
                    <div className="flex-1 text-sm" data-testid="undo-snackbar-label">{label}</div>
                    <button
                        type="button"
                        onClick={onUndo}
                        className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#FFCC00] hover:text-white px-2"
                        data-testid="undo-snackbar-undo-btn"
                    >
                        <Undo2 className="w-3.5 h-3.5" />
                        Rückgängig
                        <span className="font-mono-num ml-1 text-[10px] opacity-70">{secondsLeft}s</span>
                    </button>
                    <button
                        type="button"
                        onClick={onTimeout}
                        className="text-gray-400 hover:text-white"
                        data-testid="undo-snackbar-close"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
                <div className="h-0.5 bg-gray-700">
                    <div
                        className="h-full bg-[#FFCC00] transition-all"
                        style={{ width: `${100 - progress}%` }}
                    />
                </div>
            </div>
        </div>
    );
}
